import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_URL from '../config/api';
import useScrollAnimation from '../hooks/useScrollAnimation';
import {
  FaTree,
  FaRecycle,
  FaCubes,
  FaPuzzlePiece,
  FaTools,
  FaLeaf,
  FaStore,
  FaBoxOpen,
  FaShoppingBasket,
  FaWineBottle,
  FaBook,
  FaGift,
  FaCheckCircle
} from 'react-icons/fa';

const EasyClick = () => {
  const { t } = useTranslation();
  useScrollAnimation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/contactos`, {
        asunto: 'Consulta EasyClick - Consulta formulario web',
        nombre: formData.name,
        email: formData.email,
        telefono: formData.phone,
        mensaje: `Empresa: ${formData.company}\n\n${formData.message}`
      });

      await Swal.fire({
        icon: 'success',
        title: '¡Mensaje enviado!',
        text: 'Nos pondremos en contacto contigo pronto.',
        confirmButtonColor: '#1E5096'
      });

      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error enviando el mensaje. Por favor intenta nuevamente.',
        confirmButtonColor: '#1E5096'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Características principales de EasyClick
  const features = [
    {
      icon: FaTree,
      titleKey: 'naturalWood',
      descKey: 'naturalWoodDesc'
    },
    {
      icon: FaRecycle,
      titleKey: 'ecoFriendly',
      descKey: 'ecoFriendlyDesc'
    },
    {
      icon: FaPuzzlePiece,
      titleKey: 'modular',
      descKey: 'modularDesc'
    },
    {
      icon: FaCubes,
      titleKey: 'customizable',
      descKey: 'customizableDesc'
    },
    {
      icon: FaTools,
      titleKey: 'easyAssembly',
      descKey: 'easyAssemblyDesc'
    },
    {
      icon: FaLeaf,
      titleKey: 'durable',
      descKey: 'durableDesc'
    }
  ];

  // Tipos de exhibidores
  const displayTypes = [
    { icon: FaStore, titleKey: 'floor', descKey: 'floorDesc' },
    { icon: FaBoxOpen, titleKey: 'counter', descKey: 'counterDesc' },
    { icon: FaShoppingBasket, titleKey: 'gondolas', descKey: 'gondolasDesc' },
    { icon: FaWineBottle, titleKey: 'wineRacks', descKey: 'wineRacksDesc' },
    { icon: FaBook, titleKey: 'bookshelves', descKey: 'bookshelvesDesc' },
    { icon: FaGift, titleKey: 'promotional', descKey: 'promotionalDesc' }
  ];

  // Beneficios
  const benefitKeys = ['noTools', 'logistics', 'reconfigurable', 'premium', 'certified', 'support'];

  return (
    <div id="easyclick">
      {/* Hero Section */}
      <section className="py-5 text-white spotlight-effect" style={{
        background: 'linear-gradient(135deg, rgba(30, 80, 150, 0.9) 0%, rgba(22, 61, 115, 0.9) 100%), url("/stand-jefo.png") center/cover no-repeat',
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="mx-auto text-center animate-on-scroll">
              <div className="mb-4">
                <span
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    padding: '8px 20px',
                    borderRadius: '25px',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  {t('easyclick.badge')}
                </span>
              </div>
              <h1 className="display-3 fw-bold mb-3">{t('easyclick.title')}</h1>
              <p className="fs-4 mb-2">{t('easyclick.subtitle')}</p>
              <p className="fs-5 mb-4 opacity-75">
                {t('easyclick.heroDescription')}
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Button
                  variant="outline-light"
                  size="lg"
                  className="hero-btn"
                  onClick={() => document.getElementById('contacto-easyclick').scrollIntoView({ behavior: 'smooth' })}
                >
                  {t('easyclick.requestCatalog')}
                </Button>
                <Button
                  as={Link}
                  to="/proyectos"
                  variant="outline-light"
                  size="lg"
                  className="hero-btn"
                >
                  {t('easyclick.viewProjects')}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Características */}
      <section className="py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col className="animate-on-scroll">
              <h2 style={{ color: '#1E5096' }} className="fw-bold">{t('easyclick.whyChoose')}</h2>
              <p className="text-muted fs-5">{t('easyclick.whyChooseSubtitle')}</p>
            </Col>
          </Row>
          <Row className="g-4">
            {features.map((feature, index) => (
              <Col key={index} md={6} lg={4}>
                <div
                  className="h-100 p-4"
                  style={{
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    borderRadius: '16px',
                    border: '1px solid rgba(30, 80, 150, 0.1)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 12px 30px rgba(30, 80, 150, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div className="d-flex align-items-start">
                    <div
                      className="me-3 p-3 rounded-circle"
                      style={{ backgroundColor: 'rgba(30, 80, 150, 0.1)' }}
                    >
                      <feature.icon size={28} color="#1E5096" />
                    </div>
                    <div>
                      <h5 style={{ color: '#1E5096' }} className="fw-bold mb-2">{t(`easyclick.features.${feature.titleKey}`)}</h5>
                      <p className="text-muted mb-0">{t(`easyclick.features.${feature.descKey}`)}</p>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Tipos de Exhibidores */}
      <section className="py-5" style={{ background: '#f8f9fa' }}>
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 style={{ color: '#1E5096' }} className="fw-bold">{t('easyclick.displayTypes')}</h2>
              <p className="text-muted fs-5">{t('easyclick.displayTypesSubtitle')}</p>
            </Col>
          </Row>
          <Row className="justify-content-center g-4">
            {displayTypes.map((item, index) => (
              <Col key={index} xs={6} md={4} lg={2}>
                <div
                  className="text-center p-3"
                  style={{
                    background: '#ffffff',
                    borderRadius: '16px',
                    border: '1px solid rgba(30, 80, 150, 0.1)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    height: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 30px rgba(30, 80, 150, 0.15)';
                    e.currentTarget.style.borderColor = '#1E5096';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = 'rgba(30, 80, 150, 0.1)';
                  }}
                >
                  <div className="mb-3">
                    <item.icon size={36} color="#1E5096" />
                  </div>
                  <h6 style={{ color: '#1E5096', fontWeight: '600', fontSize: '0.85rem' }}>
                    {t(`easyclick.types.${item.titleKey}`)}
                  </h6>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {t(`easyclick.types.${item.descKey}`)}
                  </small>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Cómo Funciona */}
      <section className="py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 style={{ color: '#1E5096' }} className="fw-bold mb-4">{t('easyclick.assemblySystem')}</h2>
              <p className="text-muted fs-5 mb-4">
                {t('easyclick.assemblyDescription')}
              </p>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: '#1E5096',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}
                  >
                    1
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">{t('easyclick.step1')}</h6>
                    <small className="text-muted">{t('easyclick.step1Desc')}</small>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: '#1E5096',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}
                  >
                    2
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">{t('easyclick.step2')}</h6>
                    <small className="text-muted">{t('easyclick.step2Desc')}</small>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: '#1E5096',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}
                  >
                    3
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">{t('easyclick.step3')}</h6>
                    <small className="text-muted">{t('easyclick.step3Desc')}</small>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div
                className="p-5 text-center text-white"
                style={{
                  background: 'linear-gradient(135deg, #1E5096 0%, #163d73 100%)',
                  borderRadius: '20px'
                }}
              >
                <FaPuzzlePiece size={60} className="mb-4" />
                <h3 className="fw-bold mb-3 text-white">{t('easyclick.smartDesign')}</h3>
                <p className="mb-4">
                  {t('easyclick.smartDesignDesc')}
                </p>
                <Button
                  variant="outline-light"
                  style={{ borderRadius: '25px', padding: '8px 24px', fontWeight: '600' }}
                  onClick={() => document.getElementById('contacto-easyclick').scrollIntoView({ behavior: 'smooth' })}
                >
                  {t('easyclick.learnMore')}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Beneficios */}
      <section className="py-5" style={{ background: '#f8f9fa' }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={5} className="mb-4 mb-lg-0">
              <h2 style={{ color: '#1E5096' }} className="fw-bold">{t('easyclick.benefitsTitle')}</h2>
              <p className="text-muted">
                {t('easyclick.benefitsSubtitle')}
              </p>
            </Col>
            <Col lg={7}>
              <Row className="g-3">
                {benefitKeys.map((key, index) => (
                  <Col key={index} md={6}>
                    <div className="d-flex align-items-start">
                      <FaCheckCircle
                        className="me-2 flex-shrink-0"
                        style={{ color: '#1E5096', fontSize: '1.1rem', marginTop: '3px' }}
                      />
                      <span>{t(`easyclick.benefits.${key}`)}</span>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Formulario de Contacto */}
      <section id="contacto-easyclick" className="py-5 contact-section-bg">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="border-0 shadow">
                <Card.Body className="p-5">
                  <h2 className="text-center mb-2" style={{ color: '#1E5096' }}>
                    {t('easyclick.contactTitle')}
                  </h2>
                  <p className="text-center text-muted mb-4">
                    {t('easyclick.contactSubtitle')}
                  </p>
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('easyclick.formName')} *</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder={t('contactForm.name_placeholder')}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('easyclick.formCompany')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder={t('easyclick.formCompanyPlaceholder')}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('easyclick.formEmail')} *</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder={t('contactForm.email_placeholder')}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('easyclick.formPhone')}</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder={t('contactForm.phone_placeholder')}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-4">
                      <Form.Label>{t('easyclick.formMessage')} *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder={t('easyclick.formMessagePlaceholder')}
                      />
                    </Form.Group>
                    <div className="text-center">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                          backgroundColor: '#1E5096',
                          border: 'none',
                          borderRadius: '25px',
                          padding: '12px 40px',
                          fontWeight: '600'
                        }}
                      >
                        {isSubmitting ? t('easyclick.submitting') : t('easyclick.submitButton')}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Final */}
      <section className="py-5 text-white" style={{
        background: 'linear-gradient(135deg, #1E5096 0%, #163d73 100%)'
      }}>
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="fw-bold mb-3">{t('easyclick.ctaTitle')}</h2>
              <p className="fs-5 mb-4 opacity-75">
                {t('easyclick.ctaDescription')}
              </p>
              <Button
                as={Link}
                to="/contacto"
                variant="outline-light"
                size="lg"
                className="hero-btn"
              >
                {t('easyclick.ctaButton')}
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default EasyClick;
